import type {
  AuthMessagePayload,
  BootstrapPayload,
  LoginResponse,
  ResetPasswordPayload,
  UpdateProfilePayload
} from '../types/auth'
import type { AuthUserRole } from '../types/user'
import { normalizePermissions } from '../utils/permissions'
import type {
  AuthSetupRequired,
  BootstrapUser,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse as LoginResponseContract,
  ResetPasswordRequest,
  UpdateOwnCredentials
} from './contracts'
import { clearCsrfTokenCache, requestJson, requestVoid } from './httpClient'
import { validateAuthMessagePayload } from '../utils/apiValidation'

const AUTH_USER_ROLES: AuthUserRole[] = [
  'Admin',
  'CatalogEditor',
  'ProcurementManager',
  'Head',
  'User',
  'Storekeeper',
  'Accountant'
]
const AUTH_API_FALLBACK_ERRORS = {
  getAuthMe: 'Не удалось получить данные пользователя.',
  getAuthSetupRequired: 'Не удалось загрузить метаданные авторизации.',
  login: 'Неверный логин или пароль.',
  forgotPassword: 'Не удалось отправить инструкцию для сброса пароля.',
  resetPassword: 'Не удалось обновить пароль.',
  bootstrap: 'Ошибка инициализации.',
  updateMyProfile: 'Не удалось обновить профиль.',
  logout: 'Не удалось завершить сессию.'
} as const

const AUTH_ME_INVALID_PAYLOAD_ERROR = 'Некорректный формат ответа API: ожидались данные пользователя.'

function assertLoginResponsePayload(response: unknown): LoginResponseContract {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    throw new Error(AUTH_ME_INVALID_PAYLOAD_ERROR)
  }

  return response as LoginResponseContract
}

function isAuthUserRole(value: unknown): value is AuthUserRole {
  return typeof value === 'string' && AUTH_USER_ROLES.includes(value as AuthUserRole)
}

function normalizeLoginResponse(response: LoginResponseContract): LoginResponse {
  const role = isAuthUserRole(response.role) ? response.role : 'User'
  return {
    ...response,
    email: response.email ?? '',
    role,
    permissions: normalizePermissions(response.permissions, role)
  }
}

export function getAuthMe(apiBaseUrl: string): Promise<LoginResponse> {
  return requestJson<unknown>(`${apiBaseUrl}/api/auth/me`, {
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.getAuthMe,
    skipHandledStatuses: [401]
  }).then((response) => normalizeLoginResponse(assertLoginResponsePayload(response)))
}

export function getAuthSetupRequired(apiBaseUrl: string): Promise<AuthSetupRequired> {
  return requestJson<AuthSetupRequired>(`${apiBaseUrl}/api/auth/setup-required`, {
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.getAuthSetupRequired,
    skipHandledStatuses: [401]
  })
}

export function login(apiBaseUrl: string, credentials: LoginRequest): Promise<LoginResponse> {
  clearCsrfTokenCache()
  return requestJson<LoginResponseContract>(`${apiBaseUrl}/api/auth/login`, {
    method: 'POST',
    body: credentials satisfies LoginRequest,
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.login,
    skipHandledStatuses: [401]
  }).then((response) => {
    clearCsrfTokenCache()
    return normalizeLoginResponse(response)
  })
}

export function forgotPassword(apiBaseUrl: string, payload: ForgotPasswordRequest): Promise<AuthMessagePayload | null> {
  clearCsrfTokenCache()
  return requestJson<unknown>(`${apiBaseUrl}/api/auth/forgot-password`, {
    method: 'POST',
    body: payload satisfies ForgotPasswordRequest,
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.forgotPassword,
    skipHandledStatuses: [401]
  }).then((response) => validateAuthMessagePayload(response))
}

export function resetPassword(apiBaseUrl: string, payload: ResetPasswordPayload): Promise<AuthMessagePayload | null> {
  clearCsrfTokenCache()
  return requestJson<unknown>(`${apiBaseUrl}/api/auth/reset-password`, {
    method: 'POST',
    body: {
      token: payload.token,
      newPassword: payload.newPassword
    } satisfies ResetPasswordRequest,
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.resetPassword,
    skipHandledStatuses: [401]
  }).then((response) => validateAuthMessagePayload(response))
}

export function bootstrap(apiBaseUrl: string, payload: BootstrapPayload): Promise<AuthMessagePayload | null> {
  clearCsrfTokenCache()
  return requestJson<unknown>(`${apiBaseUrl}/api/auth/bootstrap`, {
    method: 'POST',
    body: {
      username: payload.username,
      displayName: payload.displayName || payload.username,
      password: payload.password
    } satisfies BootstrapUser,
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.bootstrap,
    skipHandledStatuses: [401]
  }).then((response) => validateAuthMessagePayload(response))
}

export function updateMyProfile(apiBaseUrl: string, payload: UpdateProfilePayload): Promise<AuthMessagePayload | null> {
  return requestJson<unknown>(`${apiBaseUrl}/api/auth/me`, {
    method: 'PATCH',
    body: payload satisfies UpdateOwnCredentials,
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.updateMyProfile,
    skipHandledStatuses: [401]
  }).then((response) => validateAuthMessagePayload(response))
}

export function logout(apiBaseUrl: string): Promise<void> {
  return requestVoid(`${apiBaseUrl}/api/auth/logout`, {
    method: 'POST',
    fallbackErrorMessage: AUTH_API_FALLBACK_ERRORS.logout,
    skipHandledStatuses: [401]
  }).finally(() => {
    clearCsrfTokenCache()
  })
}
